import {
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  useQueryParam,
  StringParam,
  NumberParam,
  withDefault
} from 'use-query-params';
import { ApiParams } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import PageTemplateCardOver from '../../../common/page-template/PageTemplateCardOver';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import HazardCreateEntityDialog from '../dialogs/HazardCreateEntityDialog';
import { useFindHazardCauses } from '../Hazard.hooks';
import { Hazard, HazardCause } from '../Hazard.interface';

export interface CauseTableProps {
  hazardId: number;
  productId: number;
  apiParams: ApiParams;
  onChange: (openDialog: boolean) => void;
  open: boolean;
}

const CausesTable: React.FC<CauseTableProps> = props => {
  const { hazardId, productId, apiParams, onChange, open } = props;
  const [openDialog, setOpenDialog] = useState<boolean>(open);
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);
  const history = useHistory();

  const {
    hazardCauseList,
    isLoadingHazardCauses,
    loadingHazardCausesError,
    findHazardCauses
  } = useFindHazardCauses(hazardId);

  useEffect(() => {
    findHazardCauses({ page, limit, order, direction, ...apiParams });
  }, [findHazardCauses, page, limit, order, direction, apiParams]);

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <PageTemplateCardOver>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'cause.id'}
                  direction={getSortDirection('cause.id')}
                  onClick={() => handleSort('cause.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'cause.title'}
                  direction={getSortDirection('cause.title')}
                  onClick={() => handleSort('cause.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'cause.createdBy'}
                  direction={getSortDirection('cause.createdBy')}
                  onClick={() => handleSort('cause.createdBy')}
                >
                  Author
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.user'}
                  direction={getSortDirection('task.user')}
                  onClick={() => handleSort('task.user')}
                >
                  Assignee
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoadingHazardCauses && (
              <TableRow>
                <TableCell colSpan={3} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {hazardCauseList &&
              hazardCauseList.data.map((cause: HazardCause) => (
                <TableRow
                  hover
                  key={cause.id}
                  onClick={() =>
                    history.push(
                      `/portal/products/${productId}/hazards/${hazardId}/causes/${cause.id}?from=causes`
                    )
                  }
                >
                  <TableCell>CAUSE-{cause.id}</TableCell>
                  <TableCell>{cause.title}</TableCell>
                  <TableCell>{`${cause.createdBy.firstName} ${cause.createdBy.lastName}`}</TableCell>
                  <TableCell>
                    {cause.task.user
                      ? `${cause.task.user?.firstName} ${cause.task.user.lastName}`
                      : 'No User Assigned'}
                  </TableCell>
                  <TableCell>
                    <TaskStatusLabel task={cause.task} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={hazardCauseList?.total || 0}
        rowsPerPage={limit}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />
      {open && (
        <HazardCreateEntityDialog
          hazardId={hazardId}
          displayName={history.location.pathname.split('/')[6]}
          onCancel={() => {
            onChange(false);
            setOpenDialog(false);
          }}
          onCreated={() => {
            onChange(false);
            setOpenDialog(false);
            findHazardCauses();
          }}
        />
      )}
      <AppSnackbar severity='error' open={Boolean(loadingHazardCausesError)}>
        There was an error loading the list of causes.
      </AppSnackbar>
    </PageTemplateCardOver>
  );
};

export default CausesTable;
